import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Calendar, Clock, ArrowRight, CarSimple, Timer, Users, Briefcase, Lock } from '@phosphor-icons/react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import InteractiveMap from './InteractiveMap';
import AddressAutocomplete from './AddressAutocomplete';
import useBookingRoute from '@/hooks/useBookingRoute';
import { addressesFromBooking, bookingCoordinates, isAddress } from '@/utils/bookingMap';
import { PremiumWifiIcon } from './VehicleFeatureBadges';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { VEHICLE_CATEGORY_CONFIG, findDispositionEstimateForCategory, findVehicleCategoryByName } from '@/utils/vehicleCategories';
import API_URL from '@/config';
import {
  createCheckoutSession,
  clearBookingCheckoutDraft,
  readBookingCheckoutDraft,
  saveBookingCheckoutDraft,
} from '@/utils/bookingCheckout';

const VEHICLE_CATEGORIES = VEHICLE_CATEGORY_CONFIG.map((category) => ({
  id: category.backendName,
  name: category.displayName,
  translationKey: category.translationKey,
  passengers: category.passengers,
  luggage: category.luggage,
  hasWifi: category.hasWifi,
  image: category.image,
  startingPrice: category.startingPrice,
}));

const BOOKING_PANEL_MIN_HEIGHT_CLASSES = 'min-h-[43.75rem] sm:min-h-[50rem] lg:min-h-[42.5rem]';
const VEHICLE_CARD_IMAGE_BG_CLASS = 'bg-[#141414]';
const DEPOSIT_PAYMENT_METHODS = new Set(['virement', 'cash']);

const BookingSection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang = 'fr' } = useParams();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [date, setDate] = useState();
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  const [time, setTime] = useState('');
  const [transferType, setTransferType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [passengerCount, setPassengerCount] = useState('1');
  const [babySeatCount, setBabySeatCount] = useState('0');
  const [dispositionHours, setDispositionHours] = useState('');
  const [dispositionPrices, setDispositionPrices] = useState([]);
  const [loadingDispositionPrices, setLoadingDispositionPrices] = useState(false);
  const [pricingCategories, setPricingCategories] = useState([]);
  const [priceEstimates, setPriceEstimates] = useState([]);
  const [estimatingPrice, setEstimatingPrice] = useState(false);
  const [priceError, setPriceError] = useState('');
  const [distanceKm, setDistanceKm] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('');
  const onRouteChange = useCallback((route) => {
    setDistanceKm(route ? String(route.distanceKm) : '');
    setDurationMinutes(route ? String(route.durationMinutes) : '');
    setPriceEstimates([]);
  }, []);
  const bookingRoute = useBookingRoute({ pickup, dropoff, setPickup, setDropoff, onRouteChange });
  const resetRoute = bookingRoute.reset;
  const [submittingCheckout, setSubmittingCheckout] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cb');
  const [bookingError, setBookingError] = useState('');
  const [bookingNotice, setBookingNotice] = useState('');
  const [fromStripeCancel, setFromStripeCancel] = useState(false);
  const autoCheckoutStartedRef = useRef(false);
  const handledStripeReturnStateRef = useRef(false);
  // Ref to access the latest submitCheckout without it being a dependency
  const submitCheckoutRef = useRef(null);
  // Ref to the form panel for smooth scroll on step changes (Bug 3b)
  const formPanelRef = useRef(null);
  const previousStepRef = useRef(1);
  const { t } = useLanguage();
  const bookingPanelMinHeight = BOOKING_PANEL_MIN_HEIGHT_CLASSES;

  const fetchPricingCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/vehicle-categories`, {
        withCredentials: true,
      });
      setPricingCategories(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('[BookingSection] Failed to fetch vehicle categories:', err);
      setPricingCategories([]);
    }
  }, []);

  const fetchDispositionPrices = useCallback(async (hours) => {
    if (!hours || parseFloat(hours) <= 0) {
      setDispositionPrices([]);
      return;
    }
    setLoadingDispositionPrices(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/estimate-price?transfer_type=disposition&disposition_hours=${parseFloat(hours)}`,
        {},
        { withCredentials: true }
      );
      setDispositionPrices(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('[BookingSection] Failed to fetch disposition prices:', err);
      setDispositionPrices([]);
    } finally {
      setLoadingDispositionPrices(false);
    }
  }, []);

  useEffect(() => {
    fetchPricingCategories();
  }, [fetchPricingCategories]);

  useEffect(() => {
    if (transferType === 'disposition' && dispositionHours && parseFloat(dispositionHours) > 0) {
      const timer = setTimeout(() => fetchDispositionPrices(dispositionHours), 500);
      return () => clearTimeout(timer);
    } else {
      setDispositionPrices([]);
    }
  }, [dispositionHours, transferType, fetchDispositionPrices]);

  // Fetch distance-based price estimates from backend whenever relevant inputs change (Bug 3a)
  useEffect(() => {
    setPriceEstimates([]);
    setPriceError('');
    if (transferType === 'disposition' || !transferType) {
      setEstimatingPrice(false);
      return;
    }
    const km = parseFloat(distanceKm);
    if (!(km > 0)) {
      setEstimatingPrice(false);
      return;
    }
    const controller = new AbortController();
    setEstimatingPrice(true);
    const params = new URLSearchParams({
      transfer_type: transferType, distance_km: String(km), duration_minutes: durationMinutes || '0',
    });
    axios
      .post(`${API_URL}/api/estimate-price?${params}`, {}, {
        withCredentials: true,
        signal: controller.signal,
      })
      .then((res) => {
        if (controller.signal.aborted) return;
        setPriceEstimates(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        if (!controller.signal.aborted && !axios.isCancel(err)) {
          console.error('[BookingSection] Failed to fetch price estimates:', err);
          setPriceError('Impossible de calculer les tarifs. Réessayez avant de confirmer.');
          setPriceEstimates([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setEstimatingPrice(false);
      });
    return () => controller.abort();
  }, [distanceKm, durationMinutes, transferType]);

  const getFormattedDispositionPrice = useCallback((categoryId) => {
    if (loadingDispositionPrices) {
      return 'Chargement...';
    }

    const price = findDispositionEstimateForCategory(dispositionPrices, categoryId);
    if (price && Number.isFinite(price.final_price)) {
      return `${price.final_price.toFixed(2)}€`;
    }

    return 'Tarif indisponible';
  }, [dispositionPrices, loadingDispositionPrices]);

  const timeSlots = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hour = h.toString().padStart(2, '0');
      const minute = m.toString().padStart(2, '0');
      timeSlots.push(`${hour}:${minute}`);
    }
  }

  const canProceedToStep2 = date && time && pickup && dropoff && transferType &&
    bookingRoute.geocoded && !bookingRoute.loading &&
    (transferType === 'disposition' ? dispositionHours : parseFloat(distanceKm) > 0);

  // Smooth scroll to the top of the booking form panel on step changes (Bug 3b)
  const scrollToFormPanel = useCallback(() => {
    window.requestAnimationFrame(() => {
      formPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const resetBookingForm = useCallback(() => {
    setDate(undefined);
    setPickup('');
    setDropoff('');
    setTime('');
    setTransferType('');
    setSelectedCategory('');
    setPassengerCount('1');
    setBabySeatCount('0');
    setDispositionHours('');
    setDistanceKm('');
    setDurationMinutes('');
    resetRoute();
    setPaymentMethod('cb');
    setDispositionPrices([]);
    setPriceEstimates([]);
    setPriceError('');
    setStep(1);
    setSubmittingCheckout(false);
    setBookingError('');
    setBookingNotice('');
    setFromStripeCancel(false);
    autoCheckoutStartedRef.current = false;
    clearBookingCheckoutDraft();
    scrollToFormPanel();
  }, [scrollToFormPanel, resetRoute]);

  const handleStep1Submit = (e) => {
    e.preventDefault();
    if (!canProceedToStep2) return;
    setStep(2);
  };

  const handleStep2Submit = (e) => {
    e.preventDefault();
    if (!selectedCategory) {
      alert('Veuillez choisir une gamme de véhicule.');
      return;
    }
    if (!user) {
      setStep('auth');
    } else {
      setStep(3);
    }
  };

  const getStartingPriceLabel = useCallback((startingPrice) => {
    const parsed = Number(String(startingPrice).replace(/[^\d.,]/g, '').replace(',', '.'));
    if (!Number.isFinite(parsed)) return startingPrice;
    const total = transferType === 'retour' ? parsed * 2 : parsed;
    return `${Math.round(total)}€`;
  }, [transferType]);

  const pricingMinFareByCategory = useMemo(
    () => Object.fromEntries(
      VEHICLE_CATEGORIES.map((category) => {
        const matchedCategory = findVehicleCategoryByName(pricingCategories, category.id);
        const minFare = Number(matchedCategory?.min_fare);
        return [category.id, Number.isFinite(minFare) ? minFare : null];
      })
    ),
    [pricingCategories]
  );

  const getCategoryStartingPrice = useCallback(
    (categoryId, fallbackStartingPrice) => pricingMinFareByCategory[categoryId] ?? fallbackStartingPrice,
    [pricingMinFareByCategory]
  );

  const getCategoryStartingPriceLabel = useCallback(
    (categoryId, fallbackStartingPrice) =>
      getStartingPriceLabel(getCategoryStartingPrice(categoryId, fallbackStartingPrice)),
    [getCategoryStartingPrice, getStartingPriceLabel]
  );

  // Returns a formatted price label for a given category in step 2 vehicle cards (Bug 3a)
  const getCategoryPriceLabel = useCallback((categoryId, fallbackStartingPrice) => {
    if (transferType === 'disposition' && dispositionHours) {
      return getFormattedDispositionPrice(categoryId);
    }
    // Show distance-based estimate if available
    if (estimatingPrice) return 'Calcul...';
    const distanceEstimate = findDispositionEstimateForCategory(priceEstimates, categoryId);
    if (distanceEstimate && Number.isFinite(Number(distanceEstimate.final_price)) && Number(distanceEstimate.final_price) > 0) {
      return `${Number(distanceEstimate.final_price).toFixed(2)}€`;
    }
    // Fall back to minimum fare label
    return `dès ${getCategoryStartingPriceLabel(categoryId, fallbackStartingPrice)}`;
  }, [transferType, dispositionHours, estimatingPrice, priceEstimates, getFormattedDispositionPrice, getCategoryStartingPriceLabel]);

  const getEstimatedPrice = useCallback(() => {
    if (!selectedCategory) return null;
    if (transferType === 'disposition') {
      const dispositionEstimate = findDispositionEstimateForCategory(dispositionPrices, selectedCategory);
      const dispositionPrice = Number(dispositionEstimate?.final_price);
      return Number.isFinite(dispositionPrice) && dispositionPrice > 0 ? dispositionPrice : null;
    }
    // Use backend estimate (distance-based) when available (Bug 3a)
    const distanceEstimate = findDispositionEstimateForCategory(priceEstimates, selectedCategory);
    const estimatedFromDistance = Number(distanceEstimate?.final_price);
    if (Number.isFinite(estimatedFromDistance) && estimatedFromDistance > 0) {
      return estimatedFromDistance;
    }
    if (parseFloat(distanceKm) > 0 || estimatingPrice) return null;
    // Fallback to min_fare (or parsed startingPrice) when no distance estimate is available
    const selectedVehicle = VEHICLE_CATEGORIES.find((c) => c.id === selectedCategory);
    if (!selectedVehicle) return null;
    const rawPriceSource = getCategoryStartingPrice(selectedVehicle.id, selectedVehicle.startingPrice);
    // Parse numeric value, stripping any currency symbols (e.g. '30€' → 30)
    const rawPrice = Number(String(rawPriceSource).replace(/[^\d.,]/g, '').replace(',', '.'));
    if (!Number.isFinite(rawPrice) || rawPrice <= 0) return null;
    return transferType === 'retour' ? rawPrice * 2 : rawPrice;
  }, [dispositionPrices, priceEstimates, selectedCategory, transferType, distanceKm, estimatingPrice, getCategoryStartingPrice]);

  const buildCheckoutPayload = useCallback(() => {
    if (!bookingRoute.geocoded || bookingRoute.loading) return null;
    const estimatedPrice = getEstimatedPrice();
    if (!estimatedPrice) return null;
    return {
      ...bookingCoordinates(bookingRoute.addresses),
      pickup_address: pickup,
      dropoff_address: dropoff,
      pickup_date: date ? format(date, 'dd/MM/yyyy', { locale: fr }) : '',
      pickup_time: time,
      transfer_type: transferType,
      vehicle_category_id: selectedCategory || null,
      distance_km: transferType === 'disposition' ? null : (distanceKm ? parseFloat(distanceKm) : null),
      duration_minutes: transferType === 'disposition' ? null : (durationMinutes ? parseFloat(durationMinutes) : null),
      estimated_price: estimatedPrice,
      passenger_count: Number(passengerCount),
      baby_seat_count: Number(babySeatCount),
      payment_method: paymentMethod,
      notes: null,
      disposition_hours: transferType === 'disposition' && dispositionHours ? parseFloat(dispositionHours) : null,
      success_path: `/${lang}/booking/confirmation`,
      cancel_path: `/${lang}/booking/cancel`,
    };
  }, [date, time, pickup, dropoff, transferType, selectedCategory, distanceKm, durationMinutes, dispositionHours, getEstimatedPrice, lang, paymentMethod, passengerCount, babySeatCount, bookingRoute.addresses, bookingRoute.geocoded, bookingRoute.loading]);

  const submitCheckout = useCallback(async (payload, draftState = null) => {
    setBookingError('');
    setBookingNotice('Vous allez être redirigé vers la plateforme de paiement sécurisée Stripe...');
    setSubmittingCheckout(true);

    const draft = draftState || {
      date: date ? date.toISOString() : null,
      pickup,
      dropoff,
      time,
      transferType,
      selectedCategory,
      passengerCount,
      babySeatCount,
      paymentMethod,
      dispositionHours,
      distanceKm,
      durationMinutes,
      mapAddresses: bookingRoute.addresses,
      step: 3,
      autoPayAfterAuth: false,
      checkoutPayload: payload,
    };
    saveBookingCheckoutDraft(draft);

    try {
      const checkout = await createCheckoutSession(payload);
      if (!checkout?.checkout_url) {
        throw new Error('URL de paiement Stripe manquante');
      }
      clearBookingCheckoutDraft();
      window.location.href = checkout.checkout_url;
    } catch (error) {
      setBookingError(error?.response?.data?.detail || 'Impossible de lancer le paiement Stripe.');
      setBookingNotice('');
      setSubmittingCheckout(false);
    }
  }, [date, pickup, dropoff, time, transferType, selectedCategory, paymentMethod, dispositionHours, distanceKm, durationMinutes, bookingRoute.addresses, passengerCount, babySeatCount]);

  const handleAuthChoice = (authPath) => {
    const payload = buildCheckoutPayload();
    if (payload) {
      saveBookingCheckoutDraft({
        date: date ? date.toISOString() : null,
        pickup,
        dropoff,
        time,
        transferType,
        selectedCategory,
        passengerCount,
        babySeatCount,
        paymentMethod,
        dispositionHours,
        distanceKm,
        durationMinutes,
        mapAddresses: bookingRoute.addresses,
        step: 3,
        autoPayAfterAuth: true,
        checkoutPayload: payload,
      });
    }
    navigate(`/${lang}/${authPath}`, {
      state: { from: { pathname: `/${lang}`, hash: '#reserver' } },
    });
  };

  // Keep submitCheckoutRef current so the auto-checkout effect can use the latest version
  // without needing to list it as a dependency (Bug 1 fix)
  useEffect(() => {
    submitCheckoutRef.current = submitCheckout;
  });

  // Effect 1: Restore draft state ONCE on mount — no reactive deps to avoid infinite loop (Bug 1 fix)
  useEffect(() => {
    const draft = readBookingCheckoutDraft();
    if (!draft) return;
    if (draft.date) setDate(new Date(draft.date));
    if (draft.pickup) setPickup(draft.pickup);
    if (draft.dropoff) setDropoff(draft.dropoff);
    if (draft.time) setTime(draft.time);
    if (draft.transferType) setTransferType(draft.transferType);
    if (draft.selectedCategory) setSelectedCategory(draft.selectedCategory);
    if (draft.passengerCount) setPassengerCount(String(draft.passengerCount));
    if (draft.babySeatCount != null) setBabySeatCount(String(draft.babySeatCount));
    if (draft.paymentMethod) setPaymentMethod(draft.paymentMethod);
    if (draft.dispositionHours) setDispositionHours(String(draft.dispositionHours));
    if (draft.distanceKm) setDistanceKm(String(draft.distanceKm));
    if (draft.durationMinutes) setDurationMinutes(String(draft.durationMinutes));
    if (draft.mapAddresses) bookingRoute.restoreAddresses(draft.mapAddresses);
    if (draft.step) setStep(draft.step);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional mount-only: adding state setters as deps would re-run on every field change, causing an infinite loop where setDate(new Date(x)) always produces a new object reference

  useEffect(() => {
    const hasStripeReturnState = Boolean(location.state?.fromBookingSuccess || location.state?.fromBookingCancel);
    if (!hasStripeReturnState) {
      handledStripeReturnStateRef.current = false;
      return;
    }
    if (handledStripeReturnStateRef.current) return;
    handledStripeReturnStateRef.current = true;

    if (location.state?.fromBookingSuccess) {
      resetBookingForm();
      navigate(`${location.pathname}${location.hash || ''}`, { replace: true, state: null });
      return;
    }

    if (location.state?.fromBookingCancel) {
      setFromStripeCancel(true);
      navigate(`${location.pathname}${location.hash || ''}`, { replace: true, state: null });
    }
  }, [location, navigate, resetBookingForm]);

  // Effect 2: Auto-checkout when user logs in after saving a draft (Bug 1 fix)
  // Depends only on `user`; uses submitCheckoutRef to avoid stale-closure issues.
  useEffect(() => {
    if (!user) return;
    const draft = readBookingCheckoutDraft();
    if (!draft?.autoPayAfterAuth || !draft?.checkoutPayload || autoCheckoutStartedRef.current) return;
    autoCheckoutStartedRef.current = true;
    const payload = draft.checkoutPayload;
    const savedAddresses = addressesFromBooking(payload);
    if (!isAddress(savedAddresses.pickup) || !isAddress(savedAddresses.dropoff)) {
      setStep(1);
      setBookingError('Veuillez sélectionner les adresses géolocalisées avant de payer.');
      return;
    }
    submitCheckoutRef.current(payload, { ...draft, autoPayAfterAuth: false });
  }, [user]);

  useEffect(() => {
    if (previousStepRef.current !== step) {
      previousStepRef.current = step;
      scrollToFormPanel();
    }
  }, [step, scrollToFormPanel]);

  useEffect(() => {
    if (location.hash === '#reserver') {
      scrollToFormPanel();
    }
  }, [location.hash, scrollToFormPanel]);

  const handleStep3Submit = async (e) => {
    e.preventDefault();
    const payload = buildCheckoutPayload();
    if (!payload) {
      setBookingError('Impossible de déterminer le montant estimé pour le paiement.');
      return;
    }

    if (!user) {
      // Safety fallback: show auth choice if user somehow reached step 3 unauthenticated
      saveBookingCheckoutDraft({
        date: date ? date.toISOString() : null,
        pickup,
        dropoff,
        time,
        transferType,
        selectedCategory,
        passengerCount,
        babySeatCount,
        paymentMethod,
        dispositionHours,
        distanceKm,
        durationMinutes,
        mapAddresses: bookingRoute.addresses,
        step: 3,
        autoPayAfterAuth: true,
        checkoutPayload: payload,
      });
      setStep('auth');
      return;
    }

    await submitCheckout(payload);
  };

  const estimatedPrice = getEstimatedPrice();
  const immediateChargeAmount = estimatedPrice == null
    ? null
    : (DEPOSIT_PAYMENT_METHODS.has(paymentMethod) ? estimatedPrice * 0.2 : estimatedPrice);
  const remainingBalance = estimatedPrice == null || immediateChargeAmount == null
    ? null
    : Math.max(estimatedPrice - immediateChargeAmount, 0);

  const stepIndicatorText = step === 'auth' ? 'Authentification' : `Étape ${step}/3`;

  return (
    <section id="reserver" className="pt-20 pb-10 md:pt-24 md:pb-14 bg-[#141414]" data-testid="booking-section">
      <div className="app-shell">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase">{t('reservationLabel')}</span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight font-['Cormorant_Garamond'] sm:text-4xl md:text-5xl" data-testid="booking-title">
            {t('bookingTitle1')}
            <br />
            <span className="gold-text">{t('bookingTitle2')}</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start lg:items-stretch">
          {/* Booking Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="w-full h-full flex flex-col"
          >
            <div ref={formPanelRef} className={`glass rounded-2xl p-5 sm:p-6 md:p-10 flex-1 flex flex-col overflow-hidden ${bookingPanelMinHeight}`}>
            {/* Step indicators */}
            <div className="mb-8 flex flex-col items-center gap-4 text-center flex-shrink-0">
              <div className="flex w-full items-center justify-center gap-2 sm:gap-3">
                {[1, 2, 3].map((s) => (
                  <div key={s} className="flex items-center">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#121212] text-sm font-semibold text-[#F3D67A] shadow-[0_10px_30px_rgba(0,0,0,0.18)]">
                      {s}
                    </div>
                    {s < 3 && <div className="mx-2 h-px w-10 bg-[#D4AF37]/35 sm:w-16 md:w-20" />}
                  </div>
                ))}
              </div>
              <span className="text-sm tracking-[0.18em] uppercase text-[#C7B588]">
                {step === 1 ? t('typeTransfert') || 'Votre trajet' : step === 2 ? 'Choisir votre véhicule' : step === 'auth' ? 'Identifiez-vous' : 'Valider votre demande'}
              </span>
            </div>

            <div className="mb-4 flex items-center justify-between">
              <span className="text-sm text-[#A1A1AA]">
                {stepIndicatorText}
              </span>
              {(step === 2 || step === 3 || step === 'auth') && (
                <button
                  type="button"
                  onClick={resetBookingForm}
                  className="text-xs text-[#D4AF37] hover:underline"
                  aria-label="Commencer une nouvelle réservation"
                  data-testid="new-booking-reset-button"
                >
                  🔄 Nouvelle réservation
                </button>
              )}
            </div>

            {fromStripeCancel && (
              <div className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3">
                <p className="mb-2 text-sm text-amber-300">
                  Votre paiement a été annulé. Vous pouvez recommencer en cliquant ci-dessous.
                </p>
                <Button onClick={resetBookingForm} variant="outline" size="sm" type="button">
                  Nouvelle réservation
                </Button>
              </div>
            )}
            {step !== 3 && bookingError && <p role="alert" className="mb-4 text-red-300">{bookingError}</p>}
            {priceError && <p role="alert" className="mb-4 text-red-300">{priceError}</p>}

            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.form
                  key="step1"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  onSubmit={handleStep1Submit}
                  className="flex-1 flex flex-col space-y-6"
                  data-testid="booking-form"
                >
                  <div className="flex flex-col gap-6">
                    {/* Date & Time */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="date" className="text-[#A1A1AA] text-sm">{t('datePriseEnCharge')}</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal bg-[#1E1E1E] border-white/10 hover:bg-[#252525] hover:border-[#D4AF37]/50"
                              data-testid="date-picker-trigger"
                            >
                              <Calendar size={18} className="mr-2 text-[#D4AF37]" />
                              {date ? format(date, 'dd/MM/yyyy', { locale: fr }) : t('choisirUneDate')}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-[#1E1E1E] border-white/10" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={date}
                              onSelect={setDate}
                              initialFocus
                              className="bg-[#1E1E1E]"
                              data-testid="calendar"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="time" className="text-[#A1A1AA] text-sm">{t('heurePriseEnCharge')}</Label>
                        <Select value={time} onValueChange={setTime}>
                          <SelectTrigger className="bg-[#1E1E1E] border-white/10 hover:border-[#D4AF37]/50" data-testid="time-select">
                            <Clock size={18} className="mr-2 text-[#D4AF37]" />
                            <SelectValue placeholder={t('choisirUneHeure')} />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1E1E1E] border-white/10 max-h-60">
                            {timeSlots.map((slot) => (
                              <SelectItem key={slot} value={slot} className="hover:bg-[#D4AF37]/10">
                                {slot}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Pickup & Dropoff */}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="pickup" className="text-[#A1A1AA] text-sm">{t('lieuPriseEnCharge')}</Label>
                        <div className="relative">
                          <MapPin size={18} className="absolute left-3 top-3 text-[#D4AF37]" />
                          <AddressAutocomplete
                            id="pickup"
                            value={pickup}
                            onChange={(value) => bookingRoute.changeAddress('pickup', value)}
                            onSelect={(address) => bookingRoute.selectAddress('pickup', address)}
                            onFocus={() => bookingRoute.setActiveField('pickup')}
                            selectedAddress={bookingRoute.addresses.pickup}
                            placeholder={t('adresseDepart')}
                            className="pl-10 bg-[#1E1E1E] border-white/10 focus:border-[#D4AF37]/50"
                            data-testid="pickup-input"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="dropoff" className="text-[#A1A1AA] text-sm">{t('lieuDepot')}</Label>
                        <div className="relative">
                          <MapPin size={18} className="absolute left-3 top-3 text-[#D4AF37]" />
                          <AddressAutocomplete
                            id="dropoff"
                            value={dropoff}
                            onChange={(value) => bookingRoute.changeAddress('dropoff', value)}
                            onSelect={(address) => bookingRoute.selectAddress('dropoff', address)}
                            onFocus={() => bookingRoute.setActiveField('dropoff')}
                            selectedAddress={bookingRoute.addresses.dropoff}
                            placeholder={t('adresseArrivee')}
                            className="pl-10 bg-[#1E1E1E] border-white/10 focus:border-[#D4AF37]/50"
                            data-testid="dropoff-input"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Transfer Type */}
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA] text-sm">{t('typeTransfert')}</Label>
                      <Select value={transferType} onValueChange={setTransferType}>
                        <SelectTrigger className="bg-[#1E1E1E] border-white/10 hover:border-[#D4AF37]/50" data-testid="transfer-type-select">
                          <SelectValue placeholder={t('choisirUnType')} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1E1E1E] border-white/10">
                          <SelectItem value="simple" className="hover:bg-[#D4AF37]/10">{t('sensUnique')}</SelectItem>
                          <SelectItem value="retour" className="hover:bg-[#D4AF37]/10">{t('allerRetour')}</SelectItem>
                          <SelectItem value="disposition" className="hover:bg-[#D4AF37]/10">{t('miseDisposition')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Disposition hours */}
                    {transferType === 'disposition' && (
                      <div className="space-y-2">
                        <Label htmlFor="disposition-hours" className="text-[#A1A1AA] text-sm flex items-center gap-2">
                          <Timer size={16} className="text-[#D4AF37]" />
                          Nombre d'heures <span className="text-red-400">*</span>
                        </Label>
                        <Input
                          id="disposition-hours"
                          type="number"
                          min="1"
                          step="0.5"
                          value={dispositionHours}
                          onChange={(e) => setDispositionHours(e.target.value)}
                          placeholder="Ex: 4"
                          className="bg-[#1E1E1E] border-white/10 focus:border-[#D4AF37]/50"
                          required
                          data-testid="disposition-hours-input"
                        />
                      </div>
                    )}

                    {/* Distance km — shown for non-disposition transfers */}
                    {transferType && transferType !== 'disposition' && (
                      <div className="space-y-2">
                        <Label htmlFor="distance-km" className="text-[#A1A1AA] text-sm flex items-center gap-2">
                          <MapPin size={16} className="text-[#D4AF37]" />
                          Distance estimée (km)
                        </Label>
                        <Input
                          id="distance-km"
                          type="number"
                          min="0"
                          step="0.1"
                          value={distanceKm}
                          readOnly={Boolean(bookingRoute.route) || bookingRoute.loading}
                          onChange={(e) => setDistanceKm(parseFloat(e.target.value) > 0 ? e.target.value : '')}
                          placeholder="Ex: 15"
                          className="bg-[#1E1E1E] border-white/10 focus:border-[#D4AF37]/50"
                          data-testid="distance-km-input"
                        />
                        <p className="text-xs text-[#A1A1AA]">
                          {bookingRoute.route
                            ? `Calcul automatique · environ ${durationMinutes} min, hors trafic.`
                            : 'Choisissez et validez les deux adresses avant de poursuivre. Distance manuelle possible si le routage est indisponible.'}
                        </p>
                      </div>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={!canProceedToStep2}
                    className="w-full bg-[#D4AF37] hover:bg-[#F0C74A] text-[#0A0A0A] font-semibold py-6 text-lg transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed mt-auto"
                    data-testid="proceed-to-vehicles"
                  >
                    Voir les véhicules disponibles
                    <ArrowRight size={20} className="ml-2" />
                  </Button>
                </motion.form>
              )}

              {step === 2 && (
                <motion.form
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                  onSubmit={handleStep2Submit}
                  className="flex-1 flex flex-col space-y-8"
                  data-testid="vehicle-selection-form"
                >
                  <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
                    {/* Recap */}
                    <div className="rounded-xl border border-[#D4AF37]/20 bg-[#1E1E1E] p-4 text-sm text-[#C7B588] space-y-1">
                      <p className="flex items-center gap-2"><MapPin size={14} className="text-[#D4AF37]" /> {pickup} → {dropoff}</p>
                      <p className="flex items-center gap-2">
                        <Calendar size={14} className="text-[#D4AF37]" />
                        {date ? `${format(date, 'dd/MM/yyyy', { locale: fr })} à ${time}` : (time || 'Date à confirmer')}
                      </p>
                      {transferType === 'disposition' && dispositionHours && (
                        <p className="flex items-center gap-2"><Timer size={14} className="text-[#D4AF37]" /> {dispositionHours}h de mise à disposition</p>
                      )}
                      {transferType !== 'disposition' && distanceKm && (
                        <p className="flex items-center gap-2"><MapPin size={14} className="text-[#D4AF37]" /> {distanceKm} km</p>
                      )}
                    </div>

                    {/* Vehicle selection */}
                    <div className="space-y-4 text-center">
                      <Label className="justify-center text-[#A1A1AA] text-sm flex items-center gap-2">
                        <CarSimple size={16} className="text-[#D4AF37]" />
                        Choisissez votre gamme
                      </Label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {VEHICLE_CATEGORIES.map((cat) => (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => {
                              setSelectedCategory(cat.id);
                              if (Number(passengerCount) > cat.passengers) {
                                setPassengerCount(String(cat.passengers));
                                setBabySeatCount(String(Math.min(Number(babySeatCount), cat.passengers)));
                              }
                            }}
                            className={`overflow-hidden rounded-2xl border text-left transition-all ${
                              selectedCategory === cat.id
                                ? 'border-[#D4AF37] bg-[#D4AF37]/10 shadow-[0_18px_40px_rgba(212,175,55,0.08)]'
                                : 'border-white/10 bg-[#1E1E1E] hover:border-[#D4AF37]/50'
                            }`}
                            data-testid={`vehicle-cat-${cat.id}`}
                          >
                            <div className={`relative aspect-[4/3] overflow-hidden ${VEHICLE_CARD_IMAGE_BG_CLASS}`}>
                              <img
                                src={cat.image}
                                alt={cat.name}
                                className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 hover:scale-105"
                              />
                              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#141414]/60 via-[#141414]/10 to-transparent" />
                            </div>
                            <div className="space-y-3 p-4">
                              <div className="flex items-center justify-between gap-3">
                                <p className="font-semibold text-sm text-white">{cat.name}</p>
                                <span className="text-xs text-[#D4AF37] font-medium">
                                  {getCategoryPriceLabel(cat.id, cat.startingPrice)}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 text-[#A1A1AA]">
                                <span className="flex items-center gap-1 text-xs">
                                  <Users size={12} className="text-[#D4AF37]" />
                                  {cat.passengers}
                                </span>
                                <span className="flex items-center gap-1 text-xs">
                                  <Briefcase size={12} className="text-[#D4AF37]" />
                                  {cat.luggage}
                                </span>
                                {cat.hasWifi && (
                                  <span className="flex items-center gap-1 text-xs">
                                    <PremiumWifiIcon size={12} className="text-[#D4AF37]" />
                                    Wi‑Fi
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {selectedCategory && (
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label className="text-[#A1A1AA] text-sm">Nombre de passagers</Label>
                          <Select
                            value={passengerCount}
                            onValueChange={(value) => {
                              setPassengerCount(value);
                              if (Number(babySeatCount) > Number(value)) setBabySeatCount(value);
                            }}
                          >
                            <SelectTrigger className="bg-[#1E1E1E] border-white/10" data-testid="passenger-count-select">
                              <SelectValue placeholder="Choisir le nombre de passagers" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1E1E1E] border-white/10">
                              {Array.from(
                                { length: VEHICLE_CATEGORIES.find((category) => category.id === selectedCategory)?.passengers || 1 },
                                (_, index) => index + 1
                              ).map((count) => (
                                <SelectItem key={count} value={String(count)}>{count}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#A1A1AA] text-sm">Sièges bébé nécessaires</Label>
                          <Select value={babySeatCount} onValueChange={setBabySeatCount}>
                            <SelectTrigger className="bg-[#1E1E1E] border-white/10" data-testid="baby-seat-count-select">
                              <SelectValue placeholder="Choisir le nombre de sièges bébé" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1E1E1E] border-white/10">
                              {Array.from({ length: Number(passengerCount) + 1 }, (_, count) => count).map((count) => (
                                <SelectItem key={count} value={String(count)}>{count}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}

                    {/* Price info */}
                    {selectedCategory && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4"
                      >
                        <p className="text-[#D4AF37] text-sm font-medium">
                          {transferType === 'disposition' && dispositionHours
                            ? `Mise à disposition · ${dispositionHours}h · ${getFormattedDispositionPrice(selectedCategory)}`
                            : `${VEHICLE_CATEGORIES.find(c => c.id === selectedCategory)?.name} · à partir de ${getCategoryStartingPriceLabel(selectedCategory, VEHICLE_CATEGORIES.find(c => c.id === selectedCategory)?.startingPrice || '')}`}
                        </p>
                        <p className="text-[#A1A1AA] text-xs mt-1">Le prix total se met à jour automatiquement selon la gamme choisie et la distance ou la durée renseignée.</p>
                      </motion.div>
                    )}
                  </div>

                  <div className="mx-auto mt-6 flex w-full max-w-4xl gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="border-white/10 text-[#A1A1AA] hover:bg-white/5"
                      onClick={() => setStep(1)}
                    >
                      Retour
                    </Button>
                    <Button
                      type="submit"
                      disabled={!selectedCategory || !estimatedPrice || estimatingPrice || loadingDispositionPrices}
                      className="flex-1 bg-[#D4AF37] hover:bg-[#F0C74A] text-[#0A0A0A] font-semibold py-6 text-lg transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="continue-booking-step-3"
                    >
                      Continuer
                      <ArrowRight size={20} className="ml-2" />
                    </Button>
                  </div>
                </motion.form>
              )}

              {step === 3 && (
                <motion.form
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                  onSubmit={handleStep3Submit}
                  className="flex-1 flex flex-col space-y-6"
                  data-testid="booking-confirmation-form"
                >
                  {bookingNotice && (
                    <div
                      className="rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-3 text-sm text-[#F3D67A]"
                      data-testid="booking-checkout-notice"
                    >
                      {bookingNotice}
                    </div>
                  )}
                  {bookingError && (
                    <div
                      className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300"
                      data-testid="booking-checkout-error"
                    >
                      {bookingError}
                    </div>
                  )}
                  <div className="space-y-4 rounded-xl border border-[#D4AF37]/20 bg-[#1E1E1E] p-5 text-sm text-[#C7B588]">
                    <p className="flex items-center gap-2"><MapPin size={14} className="text-[#D4AF37]" /> {pickup} → {dropoff}</p>
                    <p className="flex items-center gap-2">
                      <Calendar size={14} className="text-[#D4AF37]" />
                      {date ? `${format(date, 'dd/MM/yyyy', { locale: fr })} à ${time}` : (time || 'Date à confirmer')}
                    </p>
                    <p className="flex items-center gap-2"><CarSimple size={14} className="text-[#D4AF37]" /> {VEHICLE_CATEGORIES.find((c) => c.id === selectedCategory)?.name}</p>
                    <p className="flex items-center gap-2"><Users size={14} className="text-[#D4AF37]" /> {passengerCount} passager(s) · {babySeatCount} siège(s) bébé</p>
                    {transferType !== 'disposition' && distanceKm && (
                      <p className="flex items-center gap-2"><MapPin size={14} className="text-[#D4AF37]" /> Distance : {distanceKm} km</p>
                    )}
                    {transferType === 'disposition' && dispositionHours ? (
                      <p className="flex items-center gap-2">
                        <Timer size={14} className="text-[#D4AF37]" />
                        {`${dispositionHours}h · ${getFormattedDispositionPrice(selectedCategory)}`}
                      </p>
                    ) : (
                      <p className="text-[#D4AF37]">
                        Prix total: {estimatedPrice != null ? `${estimatedPrice.toFixed(2)}€` : getCategoryStartingPriceLabel(selectedCategory, VEHICLE_CATEGORIES.find((c) => c.id === selectedCategory)?.startingPrice || '')}
                      </p>
                    )}
                  </div>

                  <div className="space-y-4 rounded-xl border border-white/10 bg-[#111111] p-5">
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA] text-sm">Moyen de paiement</Label>
                      <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                        <SelectTrigger className="bg-[#1E1E1E] border-white/10 hover:border-[#D4AF37]/50" data-testid="payment-method-select">
                          <SelectValue placeholder="Choisir un moyen de paiement" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1E1E1E] border-white/10">
                          <SelectItem value="cb">Paiement immédiat par carte (Stripe)</SelectItem>
                          <SelectItem value="virement">Virement bancaire</SelectItem>
                          <SelectItem value="cash">Espèces</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {DEPOSIT_PAYMENT_METHODS.has(paymentMethod) && (
                      <div className="rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-4 py-3 text-sm text-[#F3D67A]" data-testid="deposit-payment-notice">
                        20% du montant de la course sera payé maintenant pour confirmer la réservation, le solde sera réglé par virement/espèces.
                      </div>
                    )}

                    {estimatedPrice != null && immediateChargeAmount != null && (
                      <div className="grid gap-3 text-sm text-[#C7B588] sm:grid-cols-3">
                        <div className="rounded-lg border border-white/10 bg-[#1E1E1E] px-4 py-3">
                          <p className="text-[#A1A1AA]">Montant total</p>
                          <p className="text-lg font-semibold text-white">{estimatedPrice.toFixed(2)}€</p>
                        </div>
                        <div className="rounded-lg border border-white/10 bg-[#1E1E1E] px-4 py-3">
                          <p className="text-[#A1A1AA]">{DEPOSIT_PAYMENT_METHODS.has(paymentMethod) ? 'Acompte à payer' : 'Montant à payer maintenant'}</p>
                          <p className="text-lg font-semibold text-[#D4AF37]">{immediateChargeAmount.toFixed(2)}€</p>
                        </div>
                        <div className="rounded-lg border border-white/10 bg-[#1E1E1E] px-4 py-3">
                          <p className="text-[#A1A1AA]">Solde restant</p>
                          <p className="text-lg font-semibold text-white">{(remainingBalance ?? 0).toFixed(2)}€</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3 mt-auto">
                    <Button
                      type="button"
                      variant="outline"
                      className="border-white/10 text-[#A1A1AA] hover:bg-white/5"
                      onClick={() => setStep(2)}
                    >
                      Retour
                    </Button>
                    <Button
                      type="submit"
                      disabled={submittingCheckout}
                      className="flex-1 bg-[#D4AF37] hover:bg-[#F0C74A] text-[#0A0A0A] font-semibold py-6 text-lg transition-all duration-300 hover:scale-[1.02]"
                      data-testid="submit-booking"
                    >
                      {submittingCheckout
                        ? 'Redirection vers Stripe...'
                        : DEPOSIT_PAYMENT_METHODS.has(paymentMethod)
                          ? 'Payer l’acompte et confirmer'
                          : t('reserverMaintenant')}
                      <ArrowRight size={20} className="ml-2" />
                    </Button>
                  </div>
                </motion.form>
              )}

              {step === 'auth' && (
                <motion.div
                  key="step-auth"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                  className="flex-1 flex flex-col space-y-6"
                  data-testid="auth-choice-step"
                >
                  {/* Icon + heading */}
                  <div className="flex flex-col items-center text-center space-y-3">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                      <Lock size={28} className="text-[#D4AF37]" />
                    </div>
                    <h3 className="text-xl font-semibold text-white">Identifiez-vous pour continuer</h3>
                    <p className="text-[#A1A1AA] text-sm max-w-xs">
                      Pour sécuriser votre paiement, connectez-vous ou créez un compte gratuitement. Votre réservation sera automatiquement reprise après l'authentification.
                    </p>
                  </div>

                  {/* Booking recap */}
                  <div className="rounded-xl border border-[#D4AF37]/20 bg-[#1E1E1E] p-4 text-sm text-[#C7B588] space-y-1">
                    <p className="flex items-center gap-2"><MapPin size={14} className="text-[#D4AF37]" /> {pickup} → {dropoff}</p>
                    {date && (
                      <p className="flex items-center gap-2">
                        <Calendar size={14} className="text-[#D4AF37]" />
                        {format(date, 'dd/MM/yyyy', { locale: fr })} à {time}
                      </p>
                    )}
                    <p className="flex items-center gap-2"><CarSimple size={14} className="text-[#D4AF37]" /> {VEHICLE_CATEGORIES.find((c) => c.id === selectedCategory)?.name}</p>
                  </div>

                  {/* Auth actions */}
                  <div className="flex flex-col gap-3">
                    <Button
                      type="button"
                      className="w-full bg-[#D4AF37] hover:bg-[#F0C74A] text-[#0A0A0A] font-semibold py-5 text-base"
                      onClick={() => handleAuthChoice('login')}
                      data-testid="auth-login-btn"
                    >
                      Se connecter
                      <ArrowRight size={18} className="ml-2" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 py-5 text-base"
                      onClick={() => handleAuthChoice('register')}
                      data-testid="auth-register-btn"
                    >
                      Créer un compte
                      <ArrowRight size={18} className="ml-2" />
                    </Button>
                  </div>

                  <div className="mt-auto">
                    <Button
                      type="button"
                      variant="outline"
                      className="border-white/10 text-[#A1A1AA] hover:bg-white/5"
                      onClick={() => setStep(2)}
                    >
                      Retour
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            </div>
          </motion.div>

          {/* Interactive Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className={`h-full rounded-2xl overflow-hidden ${bookingPanelMinHeight}`}
          >
            <InteractiveMap
              pickup={bookingRoute.addresses.pickup}
              dropoff={bookingRoute.addresses.dropoff}
              route={bookingRoute.route}
              activeField={bookingRoute.activeField}
              onActiveFieldChange={bookingRoute.setActiveField}
              onSelectPosition={bookingRoute.selectMapPosition}
              loading={bookingRoute.loading}
              error={bookingRoute.error}
              editable={step === 1}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;